import React, { useReducer, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { isEmployeeResponsable, getCurrentUserStaffID } from '../../utils/staff';
import { convertDecimalHoursToHoursMinutes } from '../../utils/misc';

/**
 * MUI Components
 */
import { Button, TextField, TextareaAutosize, Box, FormControlLabel, Switch } from '@mui/material';

/**
 * Components
 */
import StaffSelectControl from './Fields/StaffSelectControl';
import WorkActionAreaControl from './Fields/WorkActionAreaControl';
import WorkActionSubareaControl from './Fields/WorkActionSubareaControl';
import WorkServiceControl from './Fields/WorkServiceControl';
import StaffHourCategorySelectControl from './Fields/StaffHourCategorySelectControl';

/**
 * Contexts
 */
import { AppContext } from '../../contexts/AppContext';
import { ReportFormContext } from '../../contexts/ReportFormContext';
import { SharedFormContext } from '../../contexts/SharedFormContext';

/**
 * Models
 */
import { Employee } from '../../models/WorkReportSubmodels';

// Define the initial state
const initialState = {
    hours: '',
    minutes: '',
    notes: '',
    selectStaff: 0,
    selectService: 0,
    selectActionArea: 0,
    selectActionSubarea: 0,
    selectStaffHourCategory: 0,
    filteredStaff: [],
    filteredService: [],
    filteredActionArea: [],
    filteredActionSubarea: [],
    isFormValid: false,
    excludeFromBilling: false
};

const numericFields = ['hours', 'minutes'];

// Define the reducer function
const reducer = (state, action) => {
    switch (action.type) {
        case 'SET_FORM_DATA':
            return {
                ...state,
                [action.name]: action.value
            };
        case 'SET_FILTERED_STAFF':
            return {
                ...state,
                filteredStaff: action.filteredStaff,
            };
        case 'SET_FILTERED_SERVICE':
            return {
                ...state,
                filteredService: action.filteredService,
            };
        case 'SET_FILTERED_ACTION_AREA':
            return {
                ...state,
                filteredActionArea: action.filteredActionArea,
            };
        case 'SET_FILTERED_ACTION_SUBAREA':
            return {
                ...state,
                filteredActionSubarea: action.filteredActionSubarea,
            };
        case 'SET_FORM_VALIDITY':
            return {
                ...state,
                isFormValid: action.isFormValid
            };
        default:
            return state;
    }
};

const getEmployeeFromID = (employees, sid) => {
    for (let index = 0; index < employees.length; index++) {
        const staff = employees[index];
        if (staff.id == sid) {
            return staff;
        }
    }

    return null;
}

const convertToDecimalHours = (hours, minutes) => {
    hours = parseInt(hours, 10) || 0;
    minutes = parseInt(minutes, 10) || 0;
    let decimalMinutes = (minutes > 0) ? minutes / 60 : 0;
    let decimalHours = hours + decimalMinutes;
    return decimalHours;
};

/**
 * Add employee hours form component
 * @param {*} { onSubmit, planning }
 * @returns 
 */
const AddEmployeeHoursForm = ({ onSubmit, planning, oldData }) => {
    const { t } = useTranslation();
    const appData = useContext(AppContext);
    const { report, setReport } = useContext(ReportFormContext);
    const { updateSharedValue, getSharedValue } = useContext(SharedFormContext);
    const [state, dispatch] = useReducer(reducer, initialState);
    const sid = getCurrentUserStaffID(appData);
    const uid = appData.userInfo?.id || null;
    const isResponsable = isEmployeeResponsable(sid, planning);
    const isEdit = !!oldData;

    const handleInput = (e) => {
        const { name, value } = e.target;

        if (numericFields.includes(name)) {
            // Ensure only numeric values are allowed
            e.target.value = value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
        }
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        // For Switch components, use the checked value instead of value
        const newValue = name === 'exclude_from_billing' ? checked : value;
        dispatch({ type: 'SET_FORM_DATA', name, value: newValue });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Handle form submission
        let hours = convertToDecimalHours(state.hours, state.minutes);
        let staff = getEmployeeFromID(state.filteredStaff, state.selectStaff);
        if (hours > 0.0) {
            const employee = Employee.new();
            employee.sid = state.selectStaff;
            employee.name = staff?.full_name;
            employee.hours = hours;
            employee.notes = state.notes;
            employee.isResponsable = isResponsable;
            employee.owner = uid || 0;
            employee.workActionAreaId = state.selectActionArea;
            employee.workActionSubareaId = state.selectActionSubarea;
            employee.workServiceId = state.selectService;
            employee.hoursCategoryId = state.selectStaffHourCategory;
            employee.exclude_from_billing = state.exclude_from_billing;
            if (isEdit) {
                // Modify existing employee hours record!
                let index = report.employees.indexOf(oldData);
                report.employees[index] = employee;
                setReport({ ...report });
                console.log('Employee hours record updated with index', index);
            } else {
                // Add new employee hours record!
                report.employees.push(employee);
                setReport({ ...report });
                console.log('New employee hours record added');

                // Update shared data between forms
                updateSharedValue('selectStaff', state.selectStaff);
                updateSharedValue('selectService', state.selectService);
                updateSharedValue('selectActionArea', state.selectActionArea);
                updateSharedValue('selectActionSubarea', state.selectActionSubarea);
            }

            // Call onSubmit
            onSubmit();
        } else {
            console.warn('Cannot submit form, hours cannot be ', hours);
        }
    };

    const validateForm = () => {
        const isValid = state.selectStaff >= 0
            && (state.hours > 0 || state.minutes > 0)
            && state.selectStaffHourCategory > 0;
        dispatch({ type: 'SET_FORM_VALIDITY', isFormValid: isValid });
        // console.debug('Is form valid?', isValid, state);
    };

    // Autoset initial state from shared data if exists
    useEffect(() => {
        if (isEdit) {
            // Initialize with old data
            // console.debug('Loading data for edit...', oldData);
            let time = convertDecimalHoursToHoursMinutes(oldData.hours);
            dispatch({ type: 'SET_FORM_DATA', name: 'hours', value: time.hours });
            dispatch({ type: 'SET_FORM_DATA', name: 'minutes', value: time.minutes });
            dispatch({ type: 'SET_FORM_DATA', name: 'notes', value: oldData.notes });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectStaff', value: oldData.sid });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectService', value: oldData.workServiceId });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectActionArea', value: oldData.workActionAreaId });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: oldData.workActionSubareaId });
            dispatch({ type: 'SET_FORM_DATA', name: 'selectStaffHourCategory', value: oldData.hoursCategoryId });
            dispatch({ type: 'SET_FORM_DATA', name: 'exclude_from_billing', value: oldData.exclude_from_billing });
        } else {
            // Initialize with shared data
            if (getSharedValue('selectStaff')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectStaff', value: getSharedValue('selectStaff') });
            }
            if (getSharedValue('selectService')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectService', value: getSharedValue('selectService') });
            }
            if (getSharedValue('selectActionArea')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionArea', value: getSharedValue('selectActionArea') });
            }
            if (getSharedValue('selectActionSubarea')) {
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: getSharedValue('selectActionSubarea') });
            }
        }
    }, []);

    /**
     * Staff (Select)
     */
    useEffect(() => {
        let staff = planning?.staff || [];
        // console.log("PLANNING FROM ADD EMPLOYEE HOURS FORM", state, staff);
        if (!isResponsable) {
            // Filter staff to only include the current user if not responsible
            staff = staff.filter((employee) => employee.id === sid);
            console.info('Employee with ID', sid, 'is not responsible.');
        } else {
            console.info('Employee with ID', sid, 'is responsible for', staff);
        }
        dispatch({ type: 'SET_FILTERED_STAFF', filteredStaff: staff });
        // dispatch({ type: 'SET_FORM_DATA', name: 'selectStaff', value: sid });
    }, [planning, isResponsable, sid]);

    /**
     * Work action areas, Work action subareas & Work services (Selects)
     */
    useEffect(() => {
        console.debug('Planning loaded', planning);
        let areas = planning.work?.work_action_areas || [];
        // console.debug('Areas loaded', areas);
        dispatch({ type: 'SET_FILTERED_ACTION_AREA', filteredActionArea: areas });

        let subareas = planning.work?.work_action_subareas || [];
        // console.debug('Subareas loaded', subareas);
        dispatch({ type: 'SET_FILTERED_ACTION_SUBAREA', filteredActionSubarea: subareas });

        let services = planning.work?.catalog_services || [];
        // console.debug('Servicies loaded', services);
        dispatch({ type: 'SET_FILTERED_SERVICE', filteredService: services });
    }, [planning]);

    /**
     * Filter subareas based on current action area selected
     */
    useEffect(() => {
        if (planning?.work?.work_action_subareas) {
            const allSubareas = planning.work.work_action_subareas;
            const filtered = allSubareas.filter(
                (subarea) => subarea.in_action_areas_ids?.includes(state.selectActionArea)
            );
            dispatch({ type: 'SET_FILTERED_ACTION_SUBAREA', filteredActionSubarea: filtered });

            // Check if current subarea is valid for the new zone
            const currentSubareaValid = filtered.some(subarea => subarea.id === state.selectActionSubarea);

            if (filtered.length === 0) {
                // No subareas available for this zone
                if (state.selectActionSubarea !== 0) {
                    dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: 0 });
                    updateSharedValue('selectActionSubarea', 0);
                }
            } else if (!currentSubareaValid && state.selectActionSubarea !== 0) {
                // Current subarea is not valid for the new zone, reset it
                dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: 0 });
                updateSharedValue('selectActionSubarea', 0);
            } else if (currentSubareaValid && state.selectActionSubarea === 0) {
                // No subarea selected but there are valid options, auto-select the first one
                const firstSubarea = filtered[0];
                if (firstSubarea) {
                    dispatch({ type: 'SET_FORM_DATA', name: 'selectActionSubarea', value: firstSubarea.id });
                    updateSharedValue('selectActionSubarea', firstSubarea.id);
                }
            }
            // If current subarea is valid, keep it (no action needed)
        }
    }, [state.selectActionArea, planning]);

    /**
     * Trigger form validation on state change
     */
    useEffect(() => {
        validateForm();
        console.debug('IN HOURS FORM STATE:', state);
    }, [
        state.selectStaff,
        state.selectService,
        state.selectActionArea,
        state.selectActionSubarea,
        state.selectStaffHourCategory,
        state.hours,
        state.minutes,
        state.notes,
        state.exclude_from_billing
    ]);

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '300px', margin: '0 auto' }}>

            {/* BEGIN Shared common fields */}
            <StaffSelectControl value={getSharedValue('selectStaff', state.selectStaff || 0)} list={state.filteredStaff} handleChange={handleChange} responsableSID={planning?.responsable?.id} />
            <WorkActionAreaControl value={getSharedValue('selectActionArea', state.selectActionArea || 0)} list={state.filteredActionArea} handleChange={handleChange} showParent={false} hideSectors={true} />
            <WorkActionSubareaControl value={getSharedValue('selectActionSubarea', state.selectActionSubarea || 0)} list={state.filteredActionSubarea} handleChange={handleChange} />
            <WorkServiceControl value={getSharedValue('selectService', state.selectService || 0)} list={state.filteredService} handleChange={handleChange} />
            {/* END Shared common fields */}

            <StaffHourCategorySelectControl
                value={state.selectStaffHourCategory || 0}
                handleChange={handleChange}
            />

            <Box display="flex" gap={2} alignItems="center">
                <TextField
                    id="hours"
                    name="hours"
                    label={t('Hours')}
                    type="number"
                    inputProps={{ pattern: "[0-9]*", max: 9999 }}
                    value={state.hours}
                    onInput={handleInput}
                    onChange={handleChange}
                    fullWidth
                />

                <TextField
                    id="minutes"
                    name="minutes"
                    label={t('Minutes')}
                    type="number"
                    inputProps={{ pattern: "[0-9]*", max: 60 }}
                    value={state.minutes}
                    onInput={handleInput}
                    onChange={handleChange}
                    fullWidth
                />
            </Box>

            <FormControlLabel
                control={
                    <Switch
                        checked={state.exclude_from_billing}
                        onChange={handleChange}
                        name="exclude_from_billing" />
                }
                label={t('Heavy machinery/Truck')}
            />

            <TextareaAutosize
                id="notes"
                name="notes"
                minRows={3}
                placeholder={t('Notes')}
                value={state.notes}
                onChange={handleChange}
                style={{ width: '100%', padding: '10px' }}
            />

            <Button variant="contained" color="primary" type="submit" disabled={!state.isFormValid}>
                {isEdit ? t('Update') : t('Add')}
            </Button>
        </Box>
    );
};

AddEmployeeHoursForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    planning: PropTypes.object.isRequired,
    oldData: PropTypes.object,
};

export default AddEmployeeHoursForm;
